// How a section reads on screen. The API reports two things about a section —
// `status`, what the reviewer has decided, and `state`, the shape extraction
// left it in — and the UI collapses them into one of these. See "Extraction -
// API Contract" on Confluence (CONTENT/1365937796).
export const SECTION_STATUS = Object.freeze({
  APPROVED: 'APPROVED',
  AWAITING_PUBLISH: 'AWAITING_PUBLISH',
  // More than one candidate: the reviewer has to pick a side.
  CONFLICT: 'CONFLICT',
  // Expected section, nothing extracted for it. The reviewer types a value.
  EMPTY: 'EMPTY',
  EXCLUDED: 'EXCLUDED',
  // One candidate, still undecided.
  PENDING: 'PENDING',
  REJECTED: 'REJECTED',
})

// `status` on the section — the reviewer's decision, which outranks the shape
// extraction left it in. DRAFT means undecided, so it defers to `state`.
const REVIEW_STATUS_MAP = Object.freeze({
  APPROVED: SECTION_STATUS.APPROVED,
  AWAITING_PUBLISH: SECTION_STATUS.AWAITING_PUBLISH,
  EXCLUDED: SECTION_STATUS.EXCLUDED,
  REJECTED: SECTION_STATUS.REJECTED,
})

// `state` on the section — derived by the backend from the candidate count.
const EXTRACTION_STATE_MAP = Object.freeze({
  CONFLICT: SECTION_STATUS.CONFLICT,
  EMPTY: SECTION_STATUS.EMPTY,
  SINGLE: SECTION_STATUS.PENDING,
})

const normalise = (value) => `${value ?? ''}`.trim().toUpperCase()

// Decision first, shape second. An unrecognised pair falls back to PENDING — a
// section the reviewer still has to look at — which is the safe way to be wrong.
export const toSectionStatus = ({ state, status } = {}) =>
  REVIEW_STATUS_MAP[normalise(status)] ||
  EXTRACTION_STATE_MAP[normalise(state)] ||
  SECTION_STATUS.PENDING

// The verbs PUT /brands/{brandUuid}/sections accepts. One endpoint takes a
// list of these, so a single edit and a group approve are the same call.
export const SECTION_ACTION = Object.freeze({
  APPROVE: 'APPROVE',
  // Sets content from the request. Also how an EMPTY section gets a value.
  EDIT: 'EDIT',
  REJECT: 'REJECT',
  // Clears the selection and content, back to undecided.
  RESET: 'RESET',
  // Picks a conflict candidate and copies its text into content.
  SELECT: 'SELECT',
})

// The most items the endpoint accepts in one call, so a group approve over a
// very large group is split rather than refused.
export const SECTION_WRITE_BATCH_LIMIT = 100

// Only these read as "the reviewer has dealt with it". A conflict left
// unresolved, or a section still pending, holds the publish queue shut.
const SETTLED_SECTION_STATUSES = Object.freeze([
  SECTION_STATUS.APPROVED,
  SECTION_STATUS.AWAITING_PUBLISH,
  SECTION_STATUS.EXCLUDED,
  SECTION_STATUS.REJECTED,
])

// Counted in the "9 / 9 approved" tallies and in the publish confirmation.
const APPROVED_SECTION_STATUSES = Object.freeze([
  SECTION_STATUS.APPROVED,
  SECTION_STATUS.AWAITING_PUBLISH,
])

export const isSectionApproved = (status) =>
  APPROVED_SECTION_STATUSES.includes(status)

export const isSectionSettled = (status) =>
  SETTLED_SECTION_STATUSES.includes(status)

export const isSectionConflicted = (status) =>
  status === SECTION_STATUS.CONFLICT

// Once sections are queued for an admin the whole page turns read-only — no
// approve, reject, edit or undo until the admin acts.
export const isSectionLocked = (status) =>
  status === SECTION_STATUS.AWAITING_PUBLISH

// Shown as a chip on the right of a section header. PENDING renders no chip,
// matching the design.
export const SECTION_STATUS_LABELS = Object.freeze({
  [SECTION_STATUS.APPROVED]: 'Approved',
  [SECTION_STATUS.AWAITING_PUBLISH]: 'Awaiting publish',
  [SECTION_STATUS.CONFLICT]: 'Needs review',
  [SECTION_STATUS.EMPTY]: 'Nothing extracted',
  [SECTION_STATUS.EXCLUDED]: 'Excluded',
  [SECTION_STATUS.REJECTED]: 'Rejected',
})

export const getSectionStatusLabel = (status) =>
  SECTION_STATUS_LABELS[status] || ''

export const SECTION_CONFLICT_PROMPT =
  'Pick the value to keep before approving this group.'

export const PUBLISH_QUEUE_COPY = Object.freeze({
  blockedHint: 'Review and approve or reject all sections before sending.',
  checkingHint: 'Checking publish status…',
  queuedHeading: 'Approved sections and documents in publish queue',
  queuedHint:
    'Waiting for Admin to review and publish to Brand DNA + downstream systems.',
  readyHint: 'All sections reviewed — ready to send for Admin approval.',
  statusUnavailableHint:
    'Publish status could not be checked. Try again shortly.',
})
